import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Loader2 } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import {
  calendarEventSchema,
  type CalendarEventFormValues,
} from '@/components/Calendars/calendarSchemas'
import { useCreateCalendarEvent } from '@/hooks/useCalendarEvents'

interface AddEventDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  calendarId: string
}

export function AddEventDialog({
  open,
  onOpenChange,
  calendarId,
}: AddEventDialogProps) {
  const createEvent = useCreateCalendarEvent(calendarId)

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CalendarEventFormValues>({
    resolver: zodResolver(calendarEventSchema),
    defaultValues: {
      name: '',
      description: '',
    },
  })

  const onSubmit = (values: CalendarEventFormValues) => {
    createEvent.mutate(
      {
        name: values.name,
        description: values.description || null,
      },
      {
        onSuccess: () => {
          reset()
          onOpenChange(false)
        },
      }
    )
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      reset()
    }
    onOpenChange(nextOpen)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={createEvent.isPending ? undefined : handleOpenChange}
    >
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add Event</DialogTitle>
          <DialogDescription>
            Create a new event for this calendar.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="add-event-name">Name</Label>
            <Input
              id="add-event-name"
              {...register('name')}
              placeholder="Event name"
            />
            {errors.name && (
              <p className="text-sm text-destructive">{errors.name.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="add-event-desc">Description</Label>
            <Textarea
              id="add-event-desc"
              {...register('description')}
              placeholder="Optional description"
              rows={3}
            />
            {errors.description && (
              <p className="text-sm text-destructive">
                {errors.description.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              disabled={createEvent.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createEvent.isPending}
              className="cursor-pointer"
            >
              {createEvent.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                'Create Event'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
